import DataTable, { Column } from '@/components/CommonComponents/DataTable';
import DeleteModal from '@/components/CommonComponents/DeleteModal';
import ServerSearchFilter, {
  ServerFilterField,
} from '@/components/CommonComponents/ServerSearchFilter';
import StatCard from '@/components/CommonComponents/StatCard';
import TransactionExportCard from '@/components/WalletManagement/TransactionExportCard';
import { formatTimestamp } from '@/helper/utils';
import { useCurrencyConversion } from '@/hooks/useCurrencyConversion';
import { useDebounce } from '@/hooks/useDebounce';
import { useExportFile } from '@/hooks/useExportFile';
import { usePermission } from '@/hooks/usePermission';
import { walletService } from '@/services/walletManagementService/walletService';
import { AppDispatch, RootState } from '@/store';
import { getProjectsDropdown } from '@/store/slices/projectsSlice/projectsSlice';
import {
  getAllTransactions,
  getWalletInfoCount,
} from '@/store/slices/walletManagementSlice/walletSlice';
import { Status, WalletTransactions } from '@/types/wallet.type';
import getSymbolFromCurrency from 'currency-symbol-map';
import html2canvas from 'html2canvas';
import { AlertTriangle, Clock, DollarSign, Wallet, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { useDispatch, useSelector } from 'react-redux';
import CurrencyDropdown from '../CommonComponents/CurrencyDropdown';
import Pagination from '../CommonComponents/Pagination';
import RangeSelector from '../CommonComponents/RangeSelector';
import ViewDetailsModal from '../CommonComponents/ViewDetailsModal';
import PageHeaderWrapper from '../Layout/PageHeaderWrapper';
import UploadTransactionModal from './UploadTransactionModal';

export default function WalletManagement() {
  const dispatch = useDispatch<AppDispatch>();
  const { exportFile } = useExportFile();

  const [transactionsData, setTransactionData] = useState<WalletTransactions[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);

  const [filterState, setFilterState] = useState<Record<string, any>>({});

  const [minAmount, setMinAmount] = useState<string>('');
  const [maxAmount, setMaxAmount] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [selectedCurrency, setSelectedCurrency] = useState<string>('usd');

  const debouncedMinAmount = useDebounce(minAmount, 500);
  const debouncedMaxAmount = useDebounce(maxAmount, 500);
  const debouncedDateFrom = useDebounce(dateFrom, 300);
  const debouncedDateTo = useDebounce(dateTo, 300);

  const data = useSelector((state: RootState) => state.wallet.data);
  const walletInfoCount = useSelector((state: RootState) => state.wallet.walletInfoCount);
  const loadingTableData = useSelector((state: RootState) => state.wallet.loading);
  const totalPages = useSelector((state: RootState) => state.wallet.totalPages);
  const projectList = useSelector((state: RootState) => state.projects.list.projectsDropdown);
  const [openTxnModal, setOpenTxnModal] = useState(false);
  const [selectedTxn, setSelectedTxn] = useState<WalletTransactions | null>(null);

  const [convertedTotalCredits, setConvertedTotalCredits] = useState<number>(0);
  const [convertedAmounts, setConvertedAmounts] = useState<{ [key: string]: number }>({});
  const { convertAmount } = useCurrencyConversion();

  // Convert total credits when currency changes
  useEffect(() => {
    const convertTotalCredits = async () => {
      if (walletInfoCount?.totalCreditsUSD) {
        try {
          const converted = await convertAmount(
            walletInfoCount.totalCreditsUSD,
            'USD',
            selectedCurrency,
          );
          setConvertedTotalCredits(converted);
        } catch (error) {
          setConvertedTotalCredits(walletInfoCount.totalCreditsUSD);
        }
      }
    };

    convertTotalCredits();
  }, [walletInfoCount?.totalCreditsUSD, selectedCurrency]);

  // Convert amounts when currency changes
  useEffect(() => {
    const convertAllAmounts = async () => {
      if (!transactionsData.length) return;

      const converted: { [key: string]: number } = {};
      for (const txn of transactionsData) {
        const originalCurrency = txn.currency || 'usd';
        try {
          converted[txn.id] = await convertAmount(
            Number(txn.amount),
            originalCurrency,
            selectedCurrency,
          );
        } catch (error) {
          converted[txn.id] = Number(txn.amount);
        }
      }
      setConvertedAmounts(converted);
    };

    convertAllAmounts();
  }, [transactionsData, selectedCurrency]);

  const filters = useMemo(
    () => ({
      search: filterState.search || undefined,
      status:
        filterState.status && filterState.status !== 'All Status'
          ? (filterState.status.toUpperCase() as Status)
          : undefined,
      // type:
      //   filterState.type && filterState.type !== 'All Types'
      //     ? (filterState.type as Type)
      //     : undefined,
      projectId: filterState.project
        ? projectList?.find((p) => p.name === filterState.project)?.id
        : undefined,
      fromDate: debouncedDateFrom || undefined,
      toDate: debouncedDateTo || undefined,
      startRange: debouncedMinAmount ? Number(debouncedMinAmount) : undefined,
      endRange: debouncedMaxAmount ? Number(debouncedMaxAmount) : undefined,
      page: currentPage,
      limit: pageSize,
    }),
    [
      filterState,
      debouncedDateFrom,
      debouncedDateTo,
      debouncedMinAmount,
      debouncedMaxAmount,
      currentPage,
      pageSize,
      projectList,
    ],
  );

  useEffect(() => {
    dispatch(getProjectsDropdown());
    dispatch(getWalletInfoCount());
  }, [dispatch]);

  // useEffect(() => {
  //   try {
  //     if (filters.status) {
  //       filters.status = filters.status.toUpperCase() as Status;
  //     }
  //     dispatch(getAllTransactions(filters));
  //   } catch (error) {
  //     console.error('Error fetching transactions', error);
  //   }
  // }, [dispatch, filters]);

  // Track if initial fetch is complete to prevent duplicate calls
  const initialFetchRef = useRef(false);
  const prevFiltersRef = useRef<string>('');

  // Create a string representation of filters to detect changes
  const filtersKey = JSON.stringify({
    search: filters.search,
    status: filters.status,
    projectId: filters.projectId,
    fromDate: filters.fromDate,
    toDate: filters.toDate,
    startRange: filters.startRange,
    endRange: filters.endRange,
    page: filters.page,
    limit: filters.limit,
  });

  useEffect(() => {
    // Prevent duplicate API calls from StrictMode or dependency changes
    if (initialFetchRef.current && prevFiltersRef.current === filtersKey) return;

    // Always update the ref so dedup works on every subsequent call
    prevFiltersRef.current = filtersKey;
    initialFetchRef.current = true;

    // Only fetch transactions after projectList is loaded to avoid duplicate calls
    if (projectList !== undefined) {
      dispatch(getAllTransactions(filters));
    }
  }, [dispatch, filters, projectList, filtersKey]);

  useEffect(() => {
    const walletInfo = data?.data;
    if (walletInfo) {
      setTransactionData(walletInfo);
    }
  }, [data]);

  const projectOptions = useMemo(() => {
    if (projectList) {
      return projectList.map((project) => project.name);
    }
    return [];
  }, [projectList]);

  const filterFields: ServerFilterField[] = useMemo(
    () => [
      {
        type: 'search' as const,
        key: 'search',
        placeholder: 'Search Items...',
      },
      {
        type: 'radio-select' as const,
        key: 'status',
        label: 'Status',
        options: ['Pending', 'Completed', 'Failed'],
      },
      ...(projectOptions.length > 0
        ? [
            {
              type: 'radio-select' as const,
              key: 'project',
              label: 'Projects',
              options: projectOptions,
            },
          ]
        : []),
    ],
    [projectOptions],
  );

  const openUploadModal = () => setIsUploadOpen(true);
  const closeUploadModal = () => setIsUploadOpen(false);

  const handlePageChange = (page: number) => setCurrentPage(page);

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleExport = () => {
    const { page, limit, ...exportFilters } = filters; // remove pagination
    exportFile(() => walletService.exportWallets(exportFilters), `wallets_${Date.now()}.csv`);
  };

  const formatCurrencyAmount = (amount: number): string => {
    return amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true,
    });
  };

  const stats = {
    totalCredits: convertedTotalCredits || (walletInfoCount?.totalCreditsUSD ?? 0),
    active: walletInfoCount?.totalWallets ?? 0,
    pending: walletInfoCount?.totalPendingRewards ?? 0,
    failed: walletInfoCount?.totalFailedTransactions ?? 0,
  };

  const permissions = {
    create: usePermission('write', 'wallets'),
    edit: usePermission('edit', 'wallets'),
    export: usePermission('export', 'wallets'),
    delete: usePermission('delete', 'wallets'),
  };

  const filteredData = transactionsData?.map((each) => ({
    ...each,
    paymentStatus: each.status,
    _permissions: permissions,
  }));

  const handlePreviewTransaction = (txn: any) => {
    setSelectedTxn(txn);
    setOpenTxnModal(true);
  };

  const handleExport12 = async (txn: WalletTransactions) => {
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.top = '-9999px';

    document.body.appendChild(container);

    // Get the converted amount for this transaction
    const convertedAmount = convertedAmounts[txn.id] || Number(txn.amount);

    const root = createRoot(container);
    root.render(
      <TransactionExportCard
        txn={txn}
        selectedCurrency={selectedCurrency}
        convertedAmount={convertedAmount}
      />,
    );

    await new Promise((resolve) => setTimeout(resolve, 50));

    try {
      const canvas = await html2canvas(container, { scale: 2 });
      const image = canvas.toDataURL('image/jpeg', 1.0);

      const link = document.createElement('a');
      link.href = image;
      link.download = `transaction_${Date.now()}.jpeg`;
      link.click();
    } finally {
      root.unmount();
      document.body.removeChild(container);
    }
  };

  const getTransactionDetailFields = (txn: any) => {
    if (!txn) return [];

    return [
      {
        label: 'Wallet Transaction ID',
        value: txn.rewardTransactionId ?? txn.id ?? '--',
        half: true,
      },
      { label: 'Reference Transaction ID', value: txn.transactionId || '--', half: true },

      {
        label: 'Project',
        value: txn.projectName ? [txn.projectName] : ['--'],
        list: true,
        half: true,
      },
      {
        label: 'Survey-Set',
        value: txn.surveySetName || '--',
        half: true,
      },

      {
        label: 'Payment Mode',
        value: txn.transactionMode || '--',
        half: true,
      },
      {
        label: 'Amount',
        value: (() => {
          const symbol = getSymbolFromCurrency(selectedCurrency) || '$';
          const amount = Number(txn.amount || 0);

          return txn.type === 'CREDIT'
            ? `+${symbol}${amount.toFixed(2)}`
            : txn.type === 'DEBIT'
            ? `-${symbol}${amount.toFixed(2)}`
            : `${symbol}${amount.toFixed(2)}`;
        })(),
        half: true,
      },

      {
        label: 'Uploaded Date',
        value: txn.createdAt
          ? `${new Date(txn.createdAt).toLocaleDateString('en-GB').replace(/\//g, '-')} ${new Date(
              txn.createdAt,
            ).toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: true,
            })}`
          : '--',
        half: true,
      },

      { label: 'Status', value: txn.status || '--', fullWidth: true, badge: true, colSpan: 2 },
    ];
  };

  const walletColumns: Column<WalletTransactions>[] = [
    {
      key: 'rewardTransactionId',
      header: 'Wallet Transaction ID',
      render: (_, row) => (
        <span className="normal-case">{row.rewardTransactionId ?? row.id ?? 'N/A'}</span>
      ),
    },
    {
      key: 'transactionId',
      header: 'Reference Transaction ID',
      render: (_, row) => row.transactionId?.toUpperCase() ?? 'N/A',
    },
    {
      key: 'projectName',
      header: 'Project',
      render: (_, row) =>
        row.projectName ? (
          <span className="p-2 bg-[#C9C9C9CC] text-black text-sm px-3 rounded-2xl">
            {row.projectName}
          </span>
        ) : (
          '-'
        ),
    },
    { key: 'surveySetName', header: 'Survey Set', render: (_, row) => row.surveySetName ?? 'N/A' },
    {
      key: 'transactionMode' as keyof WalletTransactions,
      header: 'Payment Mode',
      render: (_, row) => row.transactionMode ?? 'N/A',
    },
    {
      key: 'amount',
      header: 'Amount',
      render: (_, row) => {
        if (!row.amount) return '-';
        const convertedAmount = convertedAmounts[row.id] || Number(row.amount);
        const symbol = getSymbolFromCurrency(selectedCurrency) || '$';
        return `${symbol}${Math.abs(convertedAmount).toFixed(
          2,
        )} (${selectedCurrency.toUpperCase()})`;
      },
    },
    {
      key: 'createdAt',
      header: 'Uploaded Date',
      render: (value) => <span>{formatTimestamp(value)}</span>,
    },
    { key: 'paymentStatus', header: 'Status' },
  ];

  const walletStatsData = [
    {
      id: 'total_credits',
      title: `Total Credits (${selectedCurrency.toUpperCase()})`,
      value: formatCurrencyAmount(stats.totalCredits),
      icon: DollarSign,
      iconBg: 'bg-green-500',
    },
    {
      id: 'total_wallets',
      title: 'Total Wallets',
      value: stats.active || 0,
      icon: Wallet,
      iconBg: 'bg-blue-500',
    },
    {
      id: 'pending_rewards',
      title: 'Pending Rewards',
      value: stats.pending || 0,
      icon: Clock,
      iconBg: 'bg-yellow-500',
    },
    {
      id: 'failed_transactions',
      title: 'Failed Transactions',
      value: stats.failed || 0,
      icon: AlertTriangle,
      iconBg: 'bg-red-500',
    },
  ];

  return (
    <PageHeaderWrapper
      title="Wallet Management"
      description="Monitor and manage all wallet transactions across the platform."
      onAdd={openUploadModal}
      onExport={handleExport}
      addLabel="Upload Transaction File"
      exportLabel="Export"
      permissions={permissions}
    >
      <UploadTransactionModal isOpen={isUploadOpen} onClose={closeUploadModal} />

      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex-1">
          <ServerSearchFilter
            fields={filterFields}
            onChange={(event) => {
              setFilterState(event);
              setCurrentPage(1);
            }}
            noBg
          />
        </div>
        <div className="flex flex-col min-w-[200px]">
          <div
            style={{
              borderRadius: '12px',
            }}
          >
            <CurrencyDropdown
              value={selectedCurrency}
              onChange={setSelectedCurrency}
              placeholder="Select Currency"
              className="bg-[#F2EBEE]"
            />
          </div>
        </div>
      </div>

      <div className="flex-col space-y-6 mt-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {walletStatsData.map((stat) => (
            <StatCard
              key={stat.id}
              title={stat.title}
              value={stat.value}
              icon={stat.icon}
              iconBg={stat.iconBg}
            />
          ))}
        </div>

        <div className="flex flex-wrap gap-4 items-end">
          {/* Date From */}
          <div className="flex flex-col flex-1 min-w-[200px]">
            <h3 className="text-[#242424] font-Medium text-[14px] mb-1">Date From</h3>
            <div
              className="bg-[#F2EBEE] p-3 border-[0.5px] border-[#000] rounded-lg flex items-center justify-between cursor-pointer"
              onClick={() => {
                const input = document.getElementById('dateFromInput') as HTMLInputElement | null;
                if (input) {
                  input.showPicker?.();
                  input.focus();
                }
              }}
            >
              <input
                id="dateFromInput"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="bg-transparent w-full text-sm text-gray-700 focus:outline-none cursor-pointer"
              />
            </div>
          </div>

          {/* Date To */}
          <div className="flex flex-col flex-1 min-w-[200px]">
            <h3 className="text-[#242424] font-Medium text-[14px] mb-1">Date To</h3>
            <div className="bg-[#F2EBEE] p-3 border-[0.5px] border-[#000] rounded-lg flex items-center">
              <input
                id="dateToInput"
                type="date"
                value={dateTo}
                min={dateFrom ? dateFrom : undefined}
                onChange={(e) => setDateTo(e.target.value)}
                className="bg-transparent w-full text-sm text-gray-700 focus:outline-none cursor-pointer"
              />
            </div>
          </div>

          {/* Amount Range Inputs */}
          <div className="flex flex-col flex-1">
            <RangeSelector
              min={0}
              max={100000}
              step={100}
              value={[Number(minAmount) || 0, Number(maxAmount) || 100000]}
              onChange={([min, max]) => {
                if (min === 0 && max === 100000) {
                  setMinAmount('');
                  setMaxAmount('');
                } else {
                  setMinAmount(min.toString());
                  setMaxAmount(max.toString());
                }
              }}
              label="Amount Range"
              currencySymbol={getSymbolFromCurrency(selectedCurrency) || '$'}
            />
          </div>

          {/* Clear Filter Button */}
          <div className="flex flex-col">
            <button
              onClick={() => {
                const hasFilters = dateFrom || dateTo || minAmount || maxAmount;
                if (!hasFilters) return;
                setMinAmount('');
                setMaxAmount('');
                setDateFrom('');
                setDateTo('');
                setSelectedCurrency('usd');
                setCurrentPage(1);
              }}
              className="bg-[#D0CBCD] p-2.5 border-[0.5px] px-4 border-[#000] text-[#1E1E1E] rounded-lg text-center flex items-center justify-center gap-2"
            >
              <span>Clear Filter</span>
              <X strokeWidth={1} />
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1">
          <>
            <DataTable<WalletTransactions>
              listTitle=""
              columns={walletColumns}
              data={filteredData}
              loading={loadingTableData}
              onExport={handleExport12}
              onPreview={(row) => handlePreviewTransaction(row)}
              permissions={permissions}
            />
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages || 0}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </>
          <ViewDetailsModal
            isOpen={openTxnModal}
            onClose={() => setOpenTxnModal(false)}
            title="Transaction Details"
            data={selectedTxn || {}}
            fields={getTransactionDetailFields(selectedTxn)}
          />
        </div>
      </div>

      {/* Delete Modal */}
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onDelete={() => {}}
      />
    </PageHeaderWrapper>
  );
}
